import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Grid, Typography, CircularProgress, Skeleton } from '@mui/material';
import { useDispatch } from 'react-redux';
import { GetConversionRates } from '../../redux/actions/conversionRates';
import './styles.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

const currencies = [
  { code: 'EUR', name: 'Euro' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'ILS', name: 'Israeli Shekel' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'PLN', name: 'Polish Złoty' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'AED', name: 'UAE Dirham' },
];

const steps = [
  {
    n: '01',
    title: 'Onboard',
    body: 'Standard KYB. Business documentation submitted by secure upload or email. One to three business days. No onboarding fee.',
    colorMod: 'slate',
  },
  {
    n: '02',
    title: 'Quote the payment',
    body: 'Enter the payment or submit the supplier invoice. You receive the delivered rate, the all-in cost, and the expected settlement date before you commit.',
    colorMod: 'sky',
  },
  {
    n: '03',
    title: 'Execute',
    body: 'Fund in USD. LUSID Pay converts and settles to the supplier in local currency over local rails where available, SWIFT otherwise.',
    colorMod: 'emerald',
  },
];

const coverageFacts = [
  { term: 'Priority corridors', detail: 'USD→CNY, USD→INR, USD→MXN, USD→EUR, USD→GBP.' },
  { term: 'Local payout', detail: 'EUR, GBP, INR, PHP.' },
  { term: 'SWIFT', detail: 'Available across supported currencies where local rails are not.' },
  { term: 'Currencies supported', detail: '36 currencies in 180 countries.' },
  { term: 'Domestic', detail: 'ACH origination for US-to-US business payments, live and tested.' },
  {
    term: 'Collections',
    detail: 'Inbound receipt from overseas customers with conversion to USD on the way in. Available on request.',
  },
];

const complianceFacts = [
  { term: 'Rails', detail: 'Payment services provided by The Currency Cloud Limited.' },
  { term: 'Sponsor bank', detail: "Funds held with Currencycloud's Sponsor Bank." },
  {
    term: 'Onboarding',
    detail: 'Full KYB documentation on every business account before the first transaction. No expedited path, no exceptions for referrals.',
  },
  {
    term: 'Transaction review',
    detail: 'Ambiguous transactions are reviewed against permissible-use scope before execution, in writing, and declined if outside it.',
  },
];

const exclusions = [
  'Personal remittance, including payments to family members',
  'Crypto purchase, conversion, or settlement',
  'Anything outside the permissible-use scope of the Currencycloud agreements',
  'Payments to suppliers who invoice you in USD — the supplier absorbs the conversion, so there is no saving to pass to you',
];

const FactList = ({ facts }) => (
  <Box component="dl" className="pay-facts">
    {facts.map(({ term, detail }) => (
      <Box key={term} className="pay-facts__row">
        <Typography component="dt" className="pay-facts__term">{term}</Typography>
        <Typography component="dd" className="pay-facts__detail">{detail}</Typography>
      </Box>
    ))}
  </Box>
);

const PayPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ── Currency converter state ───────────────────────────────────────────────
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const [usdAmount, setUsdAmount] = useState('');
  const [convertedAmount, setConvertedAmount] = useState('');
  const [initialRate, setInitialRate] = useState(0);
  const [fixedSide, setFixedSide] = useState('sell');
  const [conversionError, setConversionError] = useState('');
  const [rateFetching, setRateFetching] = useState(false);
  const [initialRateFetching, setInitialRateFetching] = useState(true);
  const [currencyOpen, setCurrencyOpen] = useState(false);

  const selectWrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectWrapperRef.current && !selectWrapperRef.current.contains(e.target)) {
        setCurrencyOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ── Currency conversion helpers ───────────────────────────────────────────
  const formatNumber = (value) => {
    const clean = value.replace(/[^\d.]/g, '');
    const parts = clean.split('.');
    if (parts.length > 2) return parts[0] + '.' + parts.slice(1).join('');
    if (clean.includes('.')) {
      const [integer, decimal] = clean.split('.');
      return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + decimal.slice(0, 2);
    }
    return clean.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleGetConversionRates = (currency, base_currency, amount) => {
    if (amount && amount.length > 0) {
      let query = '';
      query += `&buy_currency=${currency}`;
      query += `&sell_currency=${base_currency}`;
      query += `&fixed_side=${fixedSide}`;
      query += `&amount=${amount.replace(/[^0-9.]/g, '')}`;
      query += `&conversion_date_preference=earliest`;
      query += `&product=pay`;
      dispatch(GetConversionRates(query, setRateFetching)).then(({ res, statusCode }) => {
        if (statusCode === 200) {
          if (fixedSide === 'sell') {
            setConvertedAmount(formatNumber((res.data.clientBuyAmount / 100).toFixed(2)));
          }
          setConversionError('');
        } else {
          setConversionError(
            'The FOREX market is closed for the weekend/holiday. Please check back on the next business day when trading reopens.'
          );
          setConvertedAmount('');
          setUsdAmount('');
          setInitialRate(0);
        }
      });
    } else {
      setRateFetching(false);
    }
  };

  const handleGetInitialRates = (currency, base_currency) => {
    let query = '';
    query += `&buy_currency=${currency}`;
    query += `&sell_currency=${base_currency}`;
    query += `&fixed_side=sell`;
    query += `&amount=1`;
    query += `&conversion_date_preference=earliest`;
    query += `&product=pay`;
    dispatch(GetConversionRates(query, setInitialRateFetching)).then(({ res, statusCode }) => {
      if (statusCode === 200) {
        setInitialRate((res.data.clientBuyAmount / 100).toFixed(4));
        setConversionError('');
      } else {
        setConversionError(
          'The FOREX market is closed for the weekend/holiday. Please check back on the next business day when trading reopens.'
        );
      }
    });
  };

  const handleConversion = (e) => {
    e.preventDefault();
    handleGetConversionRates(
      selectedCurrency,
      'USD',
      fixedSide === 'sell' ? usdAmount : convertedAmount
    );
  };

  useEffect(() => {
    handleGetInitialRates(selectedCurrency, 'USD');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency]);

  const handleScrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box className="pay-page">

      {/* ── Hero + converter ─────────────────────────────────── */}
      <Box className="pay-hero">
        <Box className="pay-hero__grid-bg" />
        <Container maxWidth="lg">
          <Grid container spacing={7} alignItems="center" className="pay-hero__grid">

            <Grid item xs={12} md={6}>
              <Box className="pay-hero__content">
                <Typography className="pay-hero__overline">LUSID Pay</Typography>
                <Typography variant="h1" className="pay-hero__title">
                  Do you pay overseas suppliers in their local currency?
                </Typography>
                <Typography className="pay-hero__subtitle">
                  If you do, the exchange rate is one of your largest uncontrolled costs, and it does
                  not appear as a line item on any statement. LUSID Pay settles US business payments
                  into supplier local currency. No IT integration. No platform fee.
                </Typography>
                <Box className="pay-hero__cta-row">
                  <Button
                    variant="contained"
                    className="pay-hero__cta"
                    onClick={() => navigate('/contact-us')}
                  >
                    Talk to us about your corridor
                  </Button>
                </Box>
                <Typography className="pay-hero__note">
                  If your suppliers invoice you in USD, LUSID Pay will not save you money.{' '}
                  <Box component="span" className="pay-hero__note-link" onClick={() => handleScrollTo('pay-scope')}>
                    Here's why.
                  </Box>
                </Typography>
              </Box>
            </Grid>

            {/* ── Reference-rate converter ── */}
            <Grid item xs={12} md={6}>
              <Box className="pay-fx-card">

                <Box className="pay-fx-card__badge">
                  <span className="pay-fx-card__ping-wrapper">
                    <span className="pay-fx-card__ping" />
                    <span className="pay-fx-card__dot" />
                  </span>
                  <Typography className="pay-fx-card__badge-text">Live Competitive FX</Typography>
                </Box>

                <Typography className="pay-fx-card__title">Reference rate</Typography>

                <Box className="pay-amounts">

                  {/* USD — you send */}
                  <Box className="pay-fx-row">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography className="pay-fx-row__label">You send</Typography>
                      <input
                        type="text"
                        value={usdAmount || ''}
                        placeholder="0.00"
                        onChange={(e) => {
                          setFixedSide('sell');
                          setUsdAmount(formatNumber(e.target.value));
                          setConvertedAmount('');
                        }}
                        onKeyDown={(evt) => {
                          if (
                            !/[\d.\b]/.test(evt.key) &&
                            !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(evt.key)
                          )
                            evt.preventDefault();
                        }}
                        className="pay-fx-row__input"
                      />
                    </Box>
                    <Box className="pay-fx-row__currency-box">USD</Box>
                  </Box>

                  <Box className="pay-amounts__divider" aria-hidden="true">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </Box>

                  {/* Target currency — supplier receives */}
                  <Box className="pay-fx-row">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography className="pay-fx-row__label">Supplier receives</Typography>
                      <input
                        type="text"
                        value={convertedAmount || ''}
                        placeholder="0.00"
                        readOnly
                        className="pay-fx-row__input pay-fx-row__input--emerald pay-fx-row__input--readonly"
                      />
                    </Box>
                    <div className="pay-fx-select-wrapper" ref={selectWrapperRef}>
                      <button
                        type="button"
                        className="pay-fx-select-btn"
                        onClick={() => setCurrencyOpen((o) => !o)}
                      >
                        {selectedCurrency}
                        <svg
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          className={currencyOpen ? 'open' : ''}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {currencyOpen && (
                        <ul className="pay-fx-select-list">
                          {currencies.map(({ code, name }) => (
                            <li
                              key={code}
                              className={`pay-fx-select-list__item${selectedCurrency === code ? ' pay-fx-select-list__item--active' : ''}`}
                              onClick={() => {
                                setSelectedCurrency(code);
                                setConvertedAmount('');
                                setCurrencyOpen(false);
                              }}
                            >
                              <span className="pay-fx-select-list__code">{code}</span>
                              <span className="pay-fx-select-list__name">{name}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </Box>

                </Box>

                {/* Rate panel */}
                <Box className="pay-rate-panel">
                  <Box className="pay-rate-panel__row">
                    <Typography className="pay-rate-panel__label">Reference rate:</Typography>
                    <Typography className="pay-rate-panel__value">
                      {initialRateFetching ? (
                        <Skeleton variant="text" width={160} height={20} />
                      ) : conversionError === '' ? (
                        `1 USD = ${initialRate} ${selectedCurrency}`
                      ) : (
                        <span className="pay-rate-panel__error">{conversionError}</span>
                      )}
                    </Typography>
                  </Box>
                  <Typography className="pay-rate-panel__note">
                    This is the reference rate, not a quote and not the rate you will receive. Our
                    exchange rates are quoted at the time of conversion before you commit.
                  </Typography>
                </Box>

                <button
                  type="button"
                  disabled={rateFetching}
                  onClick={handleConversion}
                  className="pay-convert-btn"
                >
                  {rateFetching ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
                  ) : (
                    'Convert →'
                  )}
                </button>

              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* ── The problem ──────────────────────────────────────── */}
      <Box className="pay-section">
        <Container maxWidth="lg">
          <Box className="pay-section__head">
            <Typography className="pay-section__overline">The problem</Typography>
            <Typography variant="h2" className="pay-section__title">
              The wire fee is not the cost
            </Typography>
            <Typography className="pay-section__lede">
              Your bank discloses a wire fee, typically a flat amount per payment. The larger charge
              is the margin applied to the exchange rate, and it is not itemized anywhere on your
              statement. On a supplier payment of any size, that margin is usually a multiple of the
              wire fee.
            </Typography>
            <Typography className="pay-section__lede">
              LUSID Pay charges a competitive conversion rate and passes correspondent and beneficiary
              bank fees through at cost, itemized on every payment.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── How it works ─────────────────────────────────────── */}
      <Box className="pay-section pay-section--tinted" id="pay-how">
        <Container maxWidth="lg">
          <Box className="pay-section__head">
            <Typography className="pay-section__overline">How it works</Typography>
            <Typography variant="h2" className="pay-section__title">
              Three steps, no integration
            </Typography>
            <Typography className="pay-section__lede">
              LUSID Pay runs alongside your existing accounting system. There is nothing to install,
              no data migration, and no IT project to schedule.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map(({ n, title, body, colorMod }) => (
              <Grid item xs={12} md={4} key={n}>
                <Box className="pay-step">
                  <Box className={`pay-step__number pay-step__number--${colorMod}`}>{n}</Box>
                  <Typography className="pay-step__title">{title}</Typography>
                  <Typography className="pay-step__desc">{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography className="pay-section__footnote">
            Two access options: the LUSID Pay portal, or the Currencycloud portal under the Lusid
            account. Self-service or supported 1:1, depending on volume.
          </Typography>
        </Container>
      </Box>

      {/* ── Coverage ─────────────────────────────────────────── */}
      <Box className="pay-section" id="pay-coverage">
        <Container maxWidth="lg">
          <Box className="pay-section__head">
            <Typography className="pay-section__overline">Coverage</Typography>
            <Typography variant="h2" className="pay-section__title">
              Corridors and rails
            </Typography>
          </Box>
          <FactList facts={coverageFacts} />
        </Container>
      </Box>

      {/* ── Compliance ───────────────────────────────────────── */}
      <Box className="pay-section pay-section--tinted" id="pay-compliance">
        <Container maxWidth="lg">
          <Box className="pay-section__head">
            <Typography className="pay-section__overline">Compliance</Typography>
            <Typography variant="h2" className="pay-section__title">
              The discipline is the product
            </Typography>
            <Typography className="pay-section__lede">
              LUSID Pay is built by a RegTech company. Compliance posture is stated up front rather
              than buried, because the customers worth having ask about it on the first call.
            </Typography>
          </Box>
          <FactList facts={complianceFacts} />
        </Container>
      </Box>

      {/* ── Scope / exclusions ───────────────────────────────── */}
      <Box className="pay-section" id="pay-scope">
        <Container maxWidth="lg">
          <Box className="pay-section__head">
            <Typography className="pay-section__overline">Scope</Typography>
            <Typography variant="h2" className="pay-section__title">
              What LUSID Pay does not do
            </Typography>
            <Typography className="pay-section__lede">
              Stating this plainly saves everyone a call. If your use case appears below, LUSID Pay is
              not the right product and we will tell you so rather than start an onboarding.
            </Typography>
          </Box>
          <Box component="ul" className="pay-exclusions">
            {exclusions.map((item) => (
              <Box component="li" key={item} className="pay-exclusions__item">
                <span className="pay-exclusions__mark" aria-hidden="true">×</span>
                {item}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Pricing ──────────────────────────────────────────── */}
      <Box className="pay-pricing" id="pay-pricing">
        <Container maxWidth="lg">
          <Box className="pay-pricing__inner">
            <Typography className="pay-pricing__overline">Pricing &amp; exchange rate</Typography>
            <Typography variant="h2" className="pay-pricing__title">
              Disclosed before you fund
            </Typography>
            <Typography className="pay-pricing__body">
              The currency conversion rate, quoted and shown on every payment before you commit.
              Correspondent and beneficiary bank fees passed through at cost and itemized. No
              onboarding fee. No monthly platform fee. No minimum volume.
            </Typography>
            <Button
              variant="contained"
              className="pay-pricing__cta"
              onClick={() => navigate('/contact-us')}
            >
              Talk to us about your corridor
            </Button>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default PayPage;
